import { useEffect, useState } from "react";
import { Copy, Megaphone, Plus, Save, ToggleLeft, ToggleRight, X } from "lucide-react";
import { api } from "@/lib/api";
import { Btn, PageHeader } from "@/components/admin/ui";

const emptyCampaign = {
  type: "banner",
  title: "",
  body: "",
  button_text: "Shop now",
  link: "/shop",
  discount_code: "",
  accent: "#ef4444",
  enabled: false,
  frequency_days: 7,
};

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState(emptyCampaign);
  const [editingId, setEditingId] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = () => {
    api.get("/admin/campaigns").then((r) => setCampaigns(r.data)).catch(() => setCampaigns([]));
    api.get("/admin/discounts").then((r) => setDiscounts(r.data)).catch(() => setDiscounts([]));
    api.get("/admin/marketing/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
  };

  useEffect(() => {
    load();
  }, []);

  const change = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const reset = () => {
    setForm(emptyCampaign);
    setEditingId("");
    setMessage("");
  };
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      if (editingId) await api.put(`/admin/campaigns/${editingId}`, form);
      else await api.post("/admin/campaigns", form);
      reset();
      load();
    } catch (error) {
      setMessage(error?.response?.data?.detail || "Campaign could not be saved.");
    } finally {
      setSaving(false);
    }
  };
  const edit = (campaign) => {
    setEditingId(campaign.id);
    setForm({ ...emptyCampaign, ...campaign });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const toggle = async (campaign) => {
    await api.patch(`/admin/campaigns/${campaign.id}/enabled`, { enabled: !campaign.enabled });
    load();
  };

  return (
    <div className="admin-command" data-testid="campaigns-page">
      <PageHeader
        title="Campaign control"
        subtitle="Launch storefront banners and popups without changing code."
      >
        <Btn onClick={reset} data-testid="campaign-new-button">
          <Plus size={16} /> New campaign
        </Btn>
      </PageHeader>
      <div className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
        <form className="command-panel p-5 space-y-4" onSubmit={save} data-testid="campaign-form">
          <div className="flex items-center justify-between">
            <p className="command-eyebrow">{editingId ? "Edit campaign" : "Create campaign"}</p>
            {editingId && <button type="button" onClick={reset} aria-label="Close editor"><X size={18} /></button>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {["banner", "popup"].map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => change("type", type)}
                className={`command-segment ${form.type === type ? "active" : ""}`}
                data-testid={`campaign-type-${type}`}
              >
                {type}
              </button>
            ))}
          </div>
          <label className="command-label">Headline<input value={form.title} onChange={(e) => change("title", e.target.value)} required data-testid="campaign-title-input" /></label>
          <label className="command-label">Supporting text<textarea value={form.body} onChange={(e) => change("body", e.target.value)} rows="3" data-testid="campaign-body-input" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="command-label">Button<input value={form.button_text} onChange={(e) => change("button_text", e.target.value)} /></label>
            <label className="command-label">Link<input value={form.link} onChange={(e) => change("link", e.target.value)} /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="command-label">Discount code<select value={form.discount_code} onChange={(e) => change("discount_code", e.target.value)} data-testid="campaign-discount-select"><option value="">No code</option>{discounts.map((discount) => <option key={discount.id} value={discount.code}>{discount.code}</option>)}</select></label>
            <label className="command-label">Accent<input type="color" value={form.accent} onChange={(e) => change("accent", e.target.value)} data-testid="campaign-accent-input" /></label>
          </div>
          <label className="command-label">Image URL (optional)<input value={form.image_url} onChange={(e) => change("image_url", e.target.value)} data-testid="campaign-image-input" /></label>
          <div className="grid grid-cols-2 gap-3"><label className="command-label">Start date<input type="datetime-local" value={form.start_at} onChange={(e) => change("start_at", e.target.value)} /></label><label className="command-label">End date<input type="datetime-local" value={form.end_at} onChange={(e) => change("end_at", e.target.value)} /></label></div>
          {form.type === "popup" && <label className="command-label">Show again after (days)<input type="number" min="0" max="365" value={form.frequency_days} onChange={(e) => change("frequency_days", Number(e.target.value))} data-testid="campaign-frequency-input" /></label>}
          {form.type === "popup" && <div className="flex flex-wrap gap-4 text-sm text-zinc-300"><label><input type="checkbox" checked={form.collect_email} onChange={(e) => change("collect_email", e.target.checked)} /> Collect email</label><label><input type="checkbox" checked={form.collect_phone} onChange={(e) => change("collect_phone", e.target.checked)} /> Collect phone</label></div>}
          {message && <p className="text-sm text-red-300" data-testid="campaign-form-error">{message}</p>}
          <Btn type="submit" disabled={saving} data-testid="campaign-save-button"><Save size={16} /> {saving ? "Saving…" : "Save campaign"}</Btn>
        </form>
        <div className="space-y-3" data-testid="campaign-list">
          {campaigns.length === 0 && <div className="command-panel p-6 text-zinc-400">No campaigns yet.</div>}
          {campaigns.map((campaign) => (
            <div className="command-panel p-4" key={campaign.id} data-testid={`campaign-row-${campaign.id}`}>
              <div className="flex gap-3 justify-between">
                <div className="min-w-0"><p className="font-semibold truncate">{campaign.title}</p><p className="text-xs text-zinc-400 mt-1 uppercase tracking-widest">{campaign.type} · {campaign.enabled ? "Live" : "Draft"}</p></div>
                <button type="button" onClick={() => toggle(campaign)} data-testid={`campaign-toggle-${campaign.id}`} aria-label="Toggle campaign">{campaign.enabled ? <ToggleRight className="text-orange-400" /> : <ToggleLeft className="text-zinc-500" />}</button>
              </div>
              <p className="text-sm text-zinc-400 mt-3 line-clamp-2">{campaign.body || "No supporting text"}</p>
              <div className="mt-4 flex items-center gap-2"><button type="button" className="command-text-button" onClick={() => edit(campaign)}>Edit</button>{campaign.discount_code && <span className="command-code"><Copy size={12} /> {campaign.discount_code}</span>}</div>
            </div>
          ))}
          <div className="command-panel p-4" data-testid="campaign-leads"><p className="command-eyebrow">Marketing leads</p>{leads.length ? leads.slice(0, 8).map((lead) => <p className="text-sm text-zinc-300 mt-2" key={lead.id}>{lead.email || lead.phone}</p>) : <p className="text-sm text-zinc-400 mt-2">No consented contacts yet.</p>}</div>
        </div>
      </div>
    </div>
  );
}